import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  LinearProgress,
  Stack,
  Paper,
  IconButton,
  Chip,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Audiotrack as AudioIcon,
  Videocam as VideoIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Memory, MemoryCategory, MemoryFormData, User } from '../../types';
import { MemoryService } from '../../services/memoryService';
import MentionInput from '../common/MentionInput';
import { SelectedMention } from '../../types/mention';
import { mentionParser } from '../../utils/mention-parser';

interface MemoryEditorProps {
  personId: string;
  personName: string;
  currentUser: User;
  existingMemory: Memory | null;
  onSave: (memory: Memory) => void;
  onCancel?: () => void;
}

const MemoryEditor: React.FC<MemoryEditorProps> = ({
  personId,
  personName,
  currentUser,
  existingMemory,
  onSave,
  onCancel,
}) => {
  const [title, setTitle] = useState(existingMemory?.title || '');
  const [content, setContent] = useState(existingMemory?.content || '');
  const [category, setCategory] = useState<MemoryCategory>(existingMemory?.category || MemoryCategory.OTHER);
  const [isDraft, setIsDraft] = useState(existingMemory?.isDraft ?? true);
  const [selectedMentions, setSelectedMentions] = useState<SelectedMention[]>([]);

  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [existingAudioURL, setExistingAudioURL] = useState(existingMemory?.audioURL);
  const [existingVideoURL, setExistingVideoURL] = useState(existingMemory?.videoURL);

  const [isSaving, setIsSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const audioInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const maxAudioSize = 50; // MB
  const maxVideoSize = 100; // MB
  const acceptedAudioTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/m4a'];
  const acceptedVideoTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo'];

  // Handle mention change
  const handleMentionChange = useCallback((newValue: string, newMentions: SelectedMention[]) => {
    setContent(newValue);
    setSelectedMentions(newMentions);
  }, []);

  // Validate audio file
  const validateAudioFile = (file: File): string | null => {
    if (!acceptedAudioTypes.includes(file.type)) {
      return `Audio type ${file.type} is not supported. Please use: MP3, WAV, or M4A`;
    }

    if (file.size > maxAudioSize * 1024 * 1024) {
      return `Audio file size ${(file.size / 1024 / 1024).toFixed(1)}MB exceeds maximum of ${maxAudioSize}MB`;
    }

    return null;
  };

  // Validate video file
  const validateVideoFile = (file: File): string | null => {
    if (!acceptedVideoTypes.includes(file.type)) {
      return `Video type ${file.type} is not supported. Please use: MP4, MOV, or AVI`;
    }

    if (file.size > maxVideoSize * 1024 * 1024) {
      return `Video file size ${(file.size / 1024 / 1024).toFixed(1)}MB exceeds maximum of ${maxVideoSize}MB`;
    }

    return null;
  };

  // Handle audio file selection
  const handleAudioSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const validationError = validateAudioFile(file);
      if (validationError) {
        setError(validationError);
        setAudioFile(null);
        return;
      }

      setAudioFile(file);
      setError(null);
    },
    []
  );

  // Handle video file selection
  const handleVideoSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const validationError = validateVideoFile(file);
      if (validationError) {
        setError(validationError);
        setVideoFile(null);
        return;
      }

      setVideoFile(file);
      setError(null);
    },
    []
  );

  // Remove audio file
  const handleRemoveAudio = useCallback(() => {
    setAudioFile(null);
    setExistingAudioURL(undefined);
    if (audioInputRef.current) {
      audioInputRef.current.value = '';
    }
  }, []);

  // Remove video file
  const handleRemoveVideo = useCallback(() => {
    setVideoFile(null);
    setExistingVideoURL(undefined);
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
  }, []);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!title.trim()) {
      setError('Please enter a title for the memory');
      return;
    }

    if (!content.trim()) {
      setError('Please enter some content for the memory');
      return;
    }

    setIsSaving(true);
    setUploadProgress(0);
    setError(null);

    try {
      // Parse mentions from content and selected mentions
      const parsedMentions = mentionParser.parse(content, selectedMentions);

      let savedMemory: Memory;

      const formData: MemoryFormData = {
        personId,
        title: title.trim(),
        content: content.trim(),
        category,
        audioFile: audioFile || undefined,
        videoFile: videoFile || undefined,
        isDraft,
        mentions: parsedMentions,
      };

      if (existingMemory?.id) {
        // Update existing memory
        savedMemory = await MemoryService.saveMemory(
          formData,
          currentUser.uid,
          currentUser.displayName || 'Unknown',
          currentUser.role,
          existingMemory.id
        );
      } else {
        // Create new memory
        savedMemory = await MemoryService.saveMemory(
          formData,
          currentUser.uid,
          currentUser.displayName || 'Unknown',
          currentUser.role
        );
      }

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 100);

      // Complete progress
      setUploadProgress(100);
      clearInterval(progressInterval);

      // Call completion handler
      onSave(savedMemory);

      // Reset form
      setTitle('');
      setContent('');
      setCategory(MemoryCategory.OTHER);
      setIsDraft(true);
      setAudioFile(null);
      setVideoFile(null);
      setExistingAudioURL(undefined);
      setExistingVideoURL(undefined);
      setUploadProgress(0);
      setSelectedMentions([]);
    } catch (err) {
      console.error('Save error:', err);
      setError(err instanceof Error ? err.message : 'Failed to save memory');
    } finally {
      setIsSaving(false);
    }
  }, [title, content, selectedMentions, category, isDraft, personId, personName, currentUser, audioFile, videoFile, existingMemory, onSave]);

  return (
    <Box>
      <DialogTitle>
        {existingMemory ? 'Edit Memory' : 'Write a Memory'}
        {onCancel && (
          <IconButton
            aria-label="close"
            onClick={onCancel}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Error message */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Info message */}
          <Alert severity="info">
            Share a story, anecdote, or cherished memory about {personName}. You can add audio or video recordings to bring the memory to life.
          </Alert>

          {/* Upload progress */}
          {isSaving && (
            <Box>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Saving... {uploadProgress}%
              </Typography>
            </Box>
          )}

          {/* Draft toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={!isDraft}
                onChange={(e) => setIsDraft(!e.target.checked)}
                disabled={isSaving}
              />
            }
            label={isDraft ? 'Save as Draft' : 'Publish Memory'}
          />

          {/* Title */}
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            disabled={isSaving}
            helperText="A brief, descriptive title for this memory"
          />

          {/* Category */}
          <FormControl fullWidth required disabled={isSaving}>
            <InputLabel>Category</InputLabel>
            <Select value={category} label="Category" onChange={(e) => setCategory(e.target.value as MemoryCategory)}>
              <MenuItem value="childhood">Childhood</MenuItem>
              <MenuItem value="education">Education</MenuItem>
              <MenuItem value="career">Career</MenuItem>
              <MenuItem value="military">Military</MenuItem>
              <MenuItem value="marriage">Marriage</MenuItem>
              <MenuItem value="family">Family</MenuItem>
              <MenuItem value="travel">Travel</MenuItem>
              <MenuItem value="hobbies">Hobbies</MenuItem>
              <MenuItem value="achievements">Achievements</MenuItem>
              <MenuItem value="traditions">Traditions</MenuItem>
              <MenuItem value="humor">Humor</MenuItem>
              <MenuItem value="wisdom">Wisdom</MenuItem>
              <MenuItem value="relationships">Relationships</MenuItem>
              <MenuItem value="hardship">Hardship</MenuItem>
              <MenuItem value="celebration">Celebration</MenuItem>
              <MenuItem value="everyday-life">Everyday Life</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>

          {/* Content */}
          <MentionInput
            label="Memory Content"
            value={content}
            onChange={handleMentionChange}
            contentType="memory"
            targetId={personId}
            fullWidth
            required
            rows={12}
            disabled={isSaving}
            helperText="Share your memory in detail (@ to mention someone). Use paragraphs to organize your thoughts."
            placeholder="Tell the story..."
          />

          <Divider />

          {/* Audio upload */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Audio Recording (Optional)
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Add an audio recording to accompany this memory (max {maxAudioSize}MB)
            </Typography>

            {!audioFile && !existingAudioURL ? (
              <Button
                variant="outlined"
                startIcon={<AudioIcon />}
                onClick={() => audioInputRef.current?.click()}
                disabled={isSaving}
                sx={{ mt: 1 }}
              >
                Upload Audio
              </Button>
            ) : (
              <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <AudioIcon color="primary" />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {audioFile?.name || 'Existing audio recording'}
                  </Typography>
                  {audioFile && (
                    <Typography variant="caption" color="text.secondary">
                      {(audioFile.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  )}
                </Box>
                <IconButton size="small" onClick={handleRemoveAudio} disabled={isSaving}>
                  <DeleteIcon />
                </IconButton>
              </Paper>
            )}
            <input
              ref={audioInputRef}
              type="file"
              hidden
              accept={acceptedAudioTypes.join(',')}
              onChange={handleAudioSelect}
            />
          </Box>

          {/* Video upload */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Video Recording (Optional)
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Add a video recording to accompany this memory (max {maxVideoSize}MB)
            </Typography>

            {!videoFile && !existingVideoURL ? (
              <Button
                variant="outlined"
                startIcon={<VideoIcon />}
                onClick={() => videoInputRef.current?.click()}
                disabled={isSaving}
                sx={{ mt: 1 }}
              >
                Upload Video
              </Button>
            ) : (
              <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                <VideoIcon color="primary" />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {videoFile?.name || 'Existing video recording'}
                  </Typography>
                  {videoFile && (
                    <Typography variant="caption" color="text.secondary">
                      {(videoFile.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  )}
                </Box>
                <IconButton size="small" onClick={handleRemoveVideo} disabled={isSaving}>
                  <DeleteIcon />
                </IconButton>
              </Paper>
            )}
            <input
              ref={videoInputRef}
              type="file"
              hidden
              accept={acceptedVideoTypes.join(',')}
              onChange={handleVideoSelect}
            />
          </Box>

          <Typography variant="caption" color="text.secondary">
            * Required fields
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {onCancel && (
          <Button onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!title.trim() || !content.trim() || isSaving}
        >
          {isSaving ? 'Saving...' : isDraft ? 'Save Draft' : 'Publish Memory'}
        </Button>
      </DialogActions>
    </Box>
  );
};

export default MemoryEditor;
